"use client";

import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { PeriodSelector } from "@/components/ui/analytics/period-selector";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, ArrowUp, Download, Mail, Settings, AlertCircle, Info, Loader2 } from "lucide-react";
import { PageLoader } from "@/components/ui/page-loader";
import { useEmailAgents } from "@/hooks/agents/email";
import { useAgentAnalytics } from "@/hooks/agents/email/use-agent-analytics";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuth } from "@/hooks/auth/use-auth";
import { EMAIL_PERMISSIONS, EMAIL_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants";
import { AgentStatusBadge } from "../agent-status-badge";
import { exportAnalyticsToExcel } from "./export-to-excel";
import {
  OverviewCards,
  PipelineStagesChart,
  ReplyOutcomesChart,
  ClassificationBreakdown,
  ResolutionQualityCards,
  ActivityTimelineChart,
  EfficiencyMetrics,
  ToolUsageSection,
  ResponseTimeSection,
} from "./sections";

export function AgentAnalyticsDashboard() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const agentId = searchParams.get("agentId");

  const { can } = usePermissions();
  const { agents, isLoading: isLoadingAgents } = useEmailAgents();

  const agent = useMemo(
    () => agents.find((a) => a.id === agentId),
    [agents, agentId],
  );

  const { fetchWithAuth } = useAuth();
  const { data, isLoading, error, period, customRange, tz, setPeriodAndRange, refresh } =
    useAgentAnalytics({ agentId });

  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // The shell's content pane scrolls from `md` up; on a phone the document does
    // (`data-document-scroll` in globals.css), so listen to whichever one moves.
    const pane = document.querySelector<HTMLElement>("[data-shell-content]");
    const scrolls = pane && pane.scrollHeight > pane.clientHeight;
    const target: HTMLElement | Window = scrolls ? pane : window;
    scrollContainerRef.current = scrolls ? pane : null;

    const onScroll = () =>
      setShowScrollTop((scrolls ? pane.scrollTop : window.scrollY) > 400);
    target.addEventListener("scroll", onScroll, { passive: true });
    return () => target.removeEventListener("scroll", onScroll);
  }, [data]);

  const scrollToTop = useCallback(() => {
    const target = scrollContainerRef.current ?? window;
    target.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleBack = () => router.push("?page=email-agents");
  const handleViewEmails = () =>
    router.push(`?page=agent-emails&agentId=${agentId}`);
  const handleSettings = () =>
    router.push(`?page=email-agents&agentId=${agentId}`);

  const handleExport = useCallback(() => {
    if (!data || !agent) return;
    exportAnalyticsToExcel(data, agent.name, period);
  }, [data, agent, period]);

  // ---- RT exclusion callbacks ----
  const exclusionsUrl = agentId
    ? `/api/agents/email/${agentId}/analytics/exclusions`
    : null;

  const excludeMessage = useCallback(async (messageId: number) => {
    if (!exclusionsUrl) return;
    await fetchWithAuth(exclusionsUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "message", emailMessageId: messageId }),
    });
    refresh();
  }, [exclusionsUrl, fetchWithAuth, refresh]);

  const excludeSender = useCallback(async (senderEmail: string) => {
    if (!exclusionsUrl) return;
    await fetchWithAuth(exclusionsUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "sender", senderEmail }),
    });
    refresh();
  }, [exclusionsUrl, fetchWithAuth, refresh]);

  const restoreMessage = useCallback(async (messageId: number) => {
    if (!exclusionsUrl) return;
    await fetchWithAuth(exclusionsUrl, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "remove", type: "message", emailMessageId: messageId }),
    });
    refresh();
  }, [exclusionsUrl, fetchWithAuth, refresh]);

  const restoreSender = useCallback(async (senderEmail: string) => {
    if (!exclusionsUrl) return;
    await fetchWithAuth(exclusionsUrl, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "remove", type: "sender", senderEmail }),
    });
    refresh();
  }, [exclusionsUrl, fetchWithAuth, refresh]);

  const resetExclusions = useCallback(async () => {
    if (!exclusionsUrl) return;
    await fetchWithAuth(exclusionsUrl, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "reset" }),
    });
    refresh();
  }, [exclusionsUrl, fetchWithAuth, refresh]);

  const viewEmail = useCallback(async (messageId: number) => {
    const params = new URLSearchParams({ message_id: String(messageId) });
    if (agentId) params.set("agent_id", agentId);
    const res = await fetchWithAuth(`/api/agents/email/message?${params}`);
    if (!res.ok) return null;
    return res.json();
  }, [agentId, fetchWithAuth]);

  // Loading agents
  if (isLoadingAgents) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageLoader message="Loading agent..." />
      </div>
    );
  }

  // Permission check
  if (!can(EMAIL_AGENTS_PERMISSIONS.ANALYTICS)) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You don&apos;t have permission to view analytics
          </AlertDescription>
        </Alert>
        <Button onClick={handleBack} className="mt-4" variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span>Back to Agents</span>
        </Button>
      </div>
    );
  }

  // Agent not found
  if (!agent) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Agent not found</AlertDescription>
        </Alert>
        <Button onClick={handleBack} className="mt-4" variant="outline">
          <ArrowLeft className="w-4 h-4 mr-2" />
          <span>Back to Agents</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-3 split:p-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:flex-nowrap">
        <div className="flex min-w-0 flex-1 items-start gap-2 sm:items-center split:gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0 px-2 split:px-3"
            onClick={handleBack}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /><span>Back</span>
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="min-w-0 text-lg font-semibold [overflow-wrap:anywhere] split:text-2xl">{agent.name}</h1>
              <AgentStatusBadge status={agent.status} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground">Analytics Dashboard</p>
          </div>
        </div>

        <div className="order-last w-full xl:order-none xl:w-auto">
          <PeriodSelector
            period={period}
            customRange={customRange}
            onPeriodChange={setPeriodAndRange}
          />
        </div>

        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:shrink-0 sm:items-center">
          {can(EMAIL_PERMISSIONS.VIEW) && (
            <Button
              className="col-span-2"
              onClick={handleViewEmails}
            >
              <Mail className="mr-2 h-4 w-4" /><span>View Emails</span>
            </Button>
          )}

          {can(EMAIL_AGENTS_PERMISSIONS.MANAGE) && (
            <Button
              variant="outline"
              onClick={handleSettings}
            >
              <Settings className="mr-2 h-4 w-4" /><span>Settings</span>
            </Button>
          )}

          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isLoading || !data}
          >
            <Download className="mr-2 h-4 w-4" /><span>Export</span>
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Dashboard sections */}
      {data && !isLoading && (
        <>
          {/* Inbox-level metrics note */}
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0" />
            <span>Note: If this inbox is used by multiple agents, inbox-level metrics (Total Inbound,
            Pipeline Stages, Classification, Timeline, Resolution Quality) will overlap. Reply
            metrics are agent-specific.</span>
          </p>

          {/* Section 1: KPI Overview */}
          <OverviewCards cards={data.kpiCards} />

          {/* Response Time */}
          <ResponseTimeSection
            data={data.responseTimings}
            agentId={agentId!}
            config={data.responseTimeConfig}
            tz={tz}
            degraded={data.responseTimeDegraded}
            exclusions={data.rtExclusions ?? []}
            onExcludeMessage={excludeMessage}
            onExcludeSender={excludeSender}
            onRestoreMessage={restoreMessage}
            onRestoreSender={restoreSender}
            onResetExclusions={resetExclusions}
            onViewEmail={viewEmail}
          />

          {/* Section 2: Pipeline + Reply Outcomes */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <PipelineStagesChart data={data.pipelineStages} />
            <ReplyOutcomesChart data={data.replyOutcomes} />
          </div>

          {/* Section 3: Classification Breakdown */}
          <ClassificationBreakdown
            data={data.classificationBreakdown}
            categoryPerformance={data.categoryPerformance}
          />

          {/* Section 4: Activity Timeline */}
          <ActivityTimelineChart data={data.activityTimeline} />

          {/* Section 5: Resolution Quality */}
          <ResolutionQualityCards data={data.resolutionQuality} />

          {/* Section 6: Efficiency & ROI */}
          <EfficiencyMetrics
            efficiency={data.efficiency}
            breakdown={data.timeSavedBreakdown}
          />

          {/* Section 7: Tool Usage & Knowledge Base */}
          <ToolUsageSection data={data.toolUsage} />

          {/* Footer navigation */}
          <div className="flex flex-col gap-2 border-t pt-6 pb-12 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <Button variant="outline" className="w-full sm:w-auto" onClick={handleBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>Back to Agents</span>
            </Button>
            <div className="grid w-full auto-cols-fr grid-flow-col gap-2 sm:flex sm:w-auto sm:items-center">
              {can(EMAIL_PERMISSIONS.VIEW) && (
                <Button className="min-w-0 flex-1 sm:flex-none" onClick={handleViewEmails}>
                  <Mail className="w-4 h-4 mr-2" />
                  <span>View Emails</span>
                </Button>
              )}
              {can(EMAIL_AGENTS_PERMISSIONS.MANAGE) && (
                <Button variant="outline" className="min-w-0 flex-1 sm:flex-none" onClick={handleSettings}>
                  <Settings className="w-4 h-4 mr-2" />
                  <span>Settings</span>
                </Button>
              )}
            </div>
          </div>
        </>
      )}

      {/* Scroll to top */}
      {showScrollTop && (
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-6 right-6 z-50 rounded-full shadow-lg"
          onClick={scrollToTop}
        >
          <ArrowUp className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
